import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../i18n/I18nContext';
import { useNavigation } from '../../hooks/useNavigation';
import Header from './Header';
import Sidebar from './Sidebar';
import LoadingSpinner from '../ui/LoadingSpinner';
import api from '../../utils/api';

/**
 * Main application layout with header and navigation
 * Wraps all authenticated pages
 */
function AppLayout() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const { t } = useI18n();
  const [pendingCount, setPendingCount] = useState(0);

  // Load pending approvals count for admin badge
  useEffect(() => {
    if (user?.role === 'admin') {
      loadPendingCount();
    }
  }, [user]);

  async function loadPendingCount() {
    try {
      const data = await api.get('/admin/pending-approvals');
      setPendingCount(data.length);
    } catch (err) {
      console.error('Failed to load pending count:', err);
    }
  }

  // Get navigation items for the user's role
  const navItems = useNavigation(user?.role, { pendingCount });

  function handleLogout() {
    logout();
    navigate('/login');
  }

  // Get dashboard title based on role
  function getDashboardTitle() {
    if (!user) return '';
    switch (user.role) {
      case 'admin':
        return t('admin.dashboard');
      case 'owner':
        return t('owner.dashboard');
      case 'vendor':
        return t('vendor.dashboard');
      default:
        return t('common.dashboard');
    }
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return null;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--background)' }}>
      <Header title={getDashboardTitle()} onLogout={handleLogout} />
      <Sidebar navItems={navItems} />
      <main className="container" style={{ paddingTop: '16px', paddingBottom: '24px' }}>
        <Outlet context={{ user, pendingCount, refreshPendingCount: loadPendingCount }} />
      </main>
    </div>
  );
}

export default AppLayout;
